/**
 * Colis — the syndic's parcel log.
 *
 * Why this is its own page rather than a filter on /activite: a parcel writes
 * no `entry_event` at all. Nobody entered when the courier handed a box over,
 * so /activite genuinely cannot show it — and « qu'est-ce qui traîne à la
 * loge depuis une semaine » is the question this page exists to answer, which
 * no other list can.
 *
 * Age is amber from three days, the same threshold the reminder fires on and
 * the gardien's list marks — one number, computed server-side at read time so
 * the three surfaces cannot disagree.
 */
import type { RouterOutputs } from "@gately/api";
import type { ColumnDef } from "@gately/ui/data-table";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { z } from "zod/v4";

import { DataTable } from "@gately/ui/data-table";
import { DateTimePicker } from "@gately/ui/date-time-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@gately/ui/select";
import { StatusBadge } from "@gately/ui/status-badge";
import {
  PARCEL_STATUSES,
  parcelCollectorPhrase,
  parcelStatusLabel,
} from "@gately/validators";

import { dateKey } from "~/lib/dashboard-format";
import { orpc } from "~/lib/orpc";
import { resolvePermissions } from "~/lib/use-permissions";
import { useTerms } from "~/lib/use-terms";

const DATE_KEY = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

const searchSchema = z.object({
  from: DATE_KEY.optional().catch(undefined),
  to: DATE_KEY.optional().catch(undefined),
  // From the shared tuple, so a chip the toolbar renders is always a value
  // this schema keeps and the server's input schema accepts.
  status: z.enum(PARCEL_STATUSES).optional().catch(undefined),
});

type ParcelSearch = z.infer<typeof searchSchema>;

/**
 * What a `remis` row says when it can no longer name its collector — taken
 * from the shared phrase rather than retyped, so the console, the push and
 * the gardien's list degrade with the same three words.
 */
const COLLECTOR_UNKNOWN = parcelCollectorPhrase({
  handedToName: null,
  handedToLabel: null,
});

/**
 * « Tous les états » needs a value: Radix rejects an empty string as a
 * SelectItem value, and `undefined` is what the URL holds when no état is
 * chosen. The sentinel never leaves this file.
 */
const ANY_STATUS = "tous";

/** The cap `parcel.listForSyndic` applies. Mirrored here only to decide
 *  whether the truncation notice is warranted. */
const PAGE_CAP = 200;

export const Route = createFileRoute("/_dashboard/colis")({
  validateSearch: (s) => searchSchema.parse(s),
  // Same gate as /activite and /arrivees-imprevues. The server enforces it
  // too (`listForSyndic` checks canViewAllActivity) — hiding the page is a
  // courtesy, never the boundary.
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(
      orpc.me.get.queryOptions(),
    );
    if (!resolvePermissions(me).canViewActivity) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: "/" });
    }
  },
  component: ColisPage,
});

type ParcelRow = RouterOutputs["parcel"]["listForSyndic"][number];

/** Date keys go to the server as-is: it turns them into the ORGANIZATION's
 *  midnights (`dateKeyBounds`). The browser's own zone is not the building's. */
function rangeInput(search: ParcelSearch) {
  return {
    ...(search.from ? { from: search.from } : {}),
    ...(search.to ? { to: search.to } : {}),
    ...(search.status ? { status: search.status } : {}),
  };
}

/** One formatter, so « Reçu le » and « Remis le » cannot drift apart. */
function dateTimeLabel(v: string | Date): string {
  return new Date(v).toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function ColisPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const terms = useTerms();

  const query = useQuery(
    orpc.parcel.listForSyndic.queryOptions({ input: rangeInput(search) }),
  );

  // Data first, error second. React Query keeps `data` through a FAILED
  // REFETCH, so testing `isError` first would blank a fully-loaded table on a
  // transient blip — the same ordering rule /arrivees-imprevues follows.
  const rows = query.data ?? [];
  const failed = query.data === undefined && query.isError;

  function setFilter(patch: Partial<ParcelSearch>) {
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  }

  const columns: ColumnDef<ParcelRow>[] = [
    {
      accessorKey: "receivedAt",
      size: 150,
      minSize: 130,
      enableHiding: false,
      meta: { label: "Reçu le" },
      header: "Reçu le",
      cell: ({ getValue }) => (
        <span className="text-muted-foreground whitespace-nowrap">
          {dateTimeLabel(getValue<string>())}
        </span>
      ),
    },
    {
      accessorKey: "unitLabel",
      size: 130,
      minSize: 110,
      enableHiding: false,
      meta: { label: terms.unitRowLabel },
      header: terms.unitRowLabel,
    },
    {
      accessorKey: "carrier",
      size: 140,
      minSize: 110,
      meta: { label: "Transporteur" },
      header: "Transporteur",
      cell: ({ getValue }) => getValue<string | null>() ?? "—",
    },
    {
      accessorKey: "description",
      size: 200,
      minSize: 140,
      meta: { label: "Description" },
      header: "Description",
      cell: ({ getValue }) => (
        <span className="text-muted-foreground">
          {getValue<string | null>() ?? "—"}
        </span>
      ),
    },
    {
      accessorKey: "status",
      size: 130,
      minSize: 110,
      meta: { label: "État" },
      header: "État",
      // The label is vertical-aware — « À la loge » in a résidence, « À
      // l'accueil » in an office — so it comes from Terms, never a literal.
      cell: ({ getValue }) => {
        const v = getValue<ParcelRow["status"]>();
        return (
          <StatusBadge tone={v === "remis" ? "success" : "info"}>
            {parcelStatusLabel(v, terms)}
          </StatusBadge>
        );
      },
    },
    {
      accessorKey: "handedToName",
      size: 170,
      minSize: 130,
      meta: { label: "Remis à" },
      header: "Remis à",
      // « au foyer » is not decoration: `handed_to_user_id` is ON DELETE SET
      // NULL, so a hand-over whose collector account was later deleted comes
      // back with no name. The parcel WAS collected, and « — » would read as
      // « nobody » — the one thing the record must not say. A parcel still
      // waiting keeps « — », which there means exactly what it looks like.
      cell: ({ row, getValue }) => {
        const name = getValue<string | null>();
        if (name) return name;
        return row.original.status === "remis" ? (
          <span className="text-muted-foreground">{COLLECTOR_UNKNOWN}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        );
      },
    },
    {
      accessorKey: "handedAt",
      size: 150,
      minSize: 120,
      meta: { label: "Remis le" },
      header: "Remis le",
      cell: ({ getValue }) => {
        const v = getValue<string | null>();
        return (
          <span className="text-muted-foreground whitespace-nowrap">
            {v ? dateTimeLabel(v) : "—"}
          </span>
        );
      },
    },
    {
      accessorKey: "gardienName",
      size: 160,
      minSize: 130,
      meta: { label: terms.guardLabel },
      header: terms.guardLabel,
      cell: ({ getValue }) => getValue<string | null>() ?? "—",
    },
    {
      accessorKey: "ageDays",
      size: 110,
      minSize: 90,
      meta: { label: "Attente", align: "right" },
      header: "Attente",
      // Sorts on `ageDays` and reads as `sinceLabel` — « depuis 5 j », the
      // same words the gardien's list and the mobile card use, so the three
      // surfaces describe one parcel identically.
      //
      // Amber only past the reminder threshold, and only while the parcel is
      // still waiting: a parcel collected on day nine is not a problem, it is
      // a closed story. Stale rows change SHAPE as well as tone — a pill with
      // a dot against plain text — so the marker survives a greyscale print
      // and a colour-blind reader.
      cell: ({ row }) => {
        const { sinceLabel, stale, status } = row.original;
        return stale && status === "en_loge" ? (
          <StatusBadge tone="warning">{sinceLabel}</StatusBadge>
        ) : (
          <span className="text-muted-foreground">{sinceLabel}</span>
        );
      },
    },
  ];

  const isFiltered =
    search.from !== undefined ||
    search.to !== undefined ||
    search.status !== undefined;

  return (
    <div className="px-7 py-6">
      <div className="mb-5">
        <h1 className="text-foreground text-2xl font-semibold tracking-[-0.02em]">
          Colis
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Les colis laissés {terms.atTheDesk} {terms.ofTheOrg}, en attente ou
          déjà remis.
        </p>
      </div>

      <DataTable
        columns={columns}
        data={rows}
        isLoading={query.isPending}
        initialPinning={{ left: ["receivedAt"] }}
        showColumnToggle
        // État is a plain toolbar control bound to the URL, NOT a DataTable
        // faceted chip. A chip means passing `columnFilters`, and DataTable
        // shows the caller's `empty` only while no column filter is active —
        // so a failed first load under ?status=remis would have rendered its
        // generic « Aucun résultat pour ces filtres. » and told the syndic
        // that no such parcel exists. That is a statement about the loge, and
        // the network is what actually failed. Filtering is the server's job
        // here anyway, so the chip bought nothing.
        toolbar={
          <>
            <Select
              value={search.status ?? ANY_STATUS}
              onValueChange={(v) =>
                setFilter({
                  status: v === "en_loge" || v === "remis" ? v : undefined,
                })
              }
            >
              {/* The toolbar row is h-9 (the pickers, and DataTable's own
                  chips). SelectTrigger sets its height through a
                  `data-[size]` variant, so the override has to be made in the
                  same variant or the attribute selector outranks it. */}
              <SelectTrigger
                size="sm"
                className="w-40 data-[size=sm]:h-9"
                aria-label="État"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY_STATUS}>Tous les états</SelectItem>
                {PARCEL_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {parcelStatusLabel(s, terms)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <DateTimePicker
              mode="date"
              className="h-9 w-36"
              value={
                search.from ? new Date(`${search.from}T00:00:00`) : undefined
              }
              onChange={(d) => setFilter({ from: d ? dateKey(d) : undefined })}
              placeholder="Du"
            />
            <DateTimePicker
              mode="date"
              className="h-9 w-36"
              value={search.to ? new Date(`${search.to}T00:00:00`) : undefined}
              onChange={(d) => setFilter({ to: d ? dateKey(d) : undefined })}
              placeholder="Au"
            />
            {search.from || search.to ? (
              <button
                type="button"
                onClick={() => setFilter({ from: undefined, to: undefined })}
                className="text-muted-foreground hover:text-foreground text-sm font-medium transition-colors"
              >
                Effacer les dates
              </button>
            ) : null}
          </>
        }
        empty={
          // A failed load must never read as « aucun colis » — that is a
          // statement about the loge, and it would be a lie about the network.
          failed
            ? {
                title: "Impossible de charger les colis.",
                description:
                  "Vérifiez votre connexion, puis rechargez la page. Si le problème persiste, contactez Gately.",
              }
            : isFiltered
              ? {
                  title: "Aucun colis ne correspond",
                  description: "Élargissez la période ou changez l'état.",
                }
              : {
                  title: "Aucun colis enregistré.",
                  description: `Chaque colis laissé ${terms.atTheDesk} apparaît ici dès que ${terms.guardLabel.toLowerCase()} l'enregistre.`,
                }
        }
        footer={
          rows.length === PAGE_CAP ? (
            <>
              Affichage limité aux {PAGE_CAP} colis les plus récents — resserrez
              la période pour voir les plus anciens.
            </>
          ) : null
        }
      />
    </div>
  );
}
